import { motion } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  Sparkles,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import DataFlowDiagram from "../../Components/DataFlow/DataFlowDiagram";
import {
  CORE_STRATEGY_POINTS,
  EMAIL_LIST_ADVANTAGES,
  CAMPAIGN_WORKFLOW,
} from "../../Data/b2bCompanyData";

import "../../Style/services/b2b-company-data.css";

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -6, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function B2BCompanyData() {
  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="b2b-company-page">
      <Seo
        title="Organized B2B Company Data for Higher ROI | Conectify Tech"
        description="Identify ideal company profiles across 50+ industries and 170+ countries with AI and human-verified firmographic data from Conectify Tech."
      />

      {/* Hero Section */}
      <section className="b2b-comp-hero">
        <Container className="b2b-comp-hero__container">
          <motion.div
            className="b2b-comp-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">
              <Sparkles size={14} />
              <span>Verified Enterprise Firmographics</span>
            </div>

            <h1 className="b2b-comp-hero__title">
              Organized <span className="text-gradient">B2B Company Data</span> for Business Greater ROI
            </h1>

            <p className="b2b-comp-hero__lead">
              Identify ideal company profiles with the best B2B company data provider. Customize your
              B2B company data and benefit from human intelligence and proprietary AI verification to
              reach the right audience. Connect with your ideal audience and target markets from 50+
              industries and 170+ countries.
            </p>

            <div className="b2b-comp-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                Request Company Data
              </Button>
              <Button variant="secondary" onClick={handleConsultation}>
                Explore Global Coverage
              </Button>
            </div>
          </motion.div>

          {/* Interactive 3D Architecture Diagram */}
          <motion.div
            className="b2b-comp-hero__board"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <DataFlowDiagram />
          </motion.div>
        </Container>
      </section>

      {/* Real-Time Business Insights in Action */}
      <section className="b2b-comp-insights">
        <Container>
          <div className="b2b-comp-insights__card">
            <div className="section-head text-center">
              <span className="section-eyebrow">Intel In Action</span>
              <h2 className="section-title">
                Transform with Conectify Tech. Real-time Business Insights in Action.
              </h2>
            </div>

            <p className="b2b-comp-insights__detail">
              Industries flourish on business insights, intent data, and human intelligence. It can
              help you analyze your competitors or products and their performance. Company Insights
              can also open up new investment opportunities to grow your business. Therefore, the data
              must be valid and of high quality. Out of date or unresponsive data is just consuming
              your resources and funds. To ensure the information you receive is actionable, we rely
              on our data scientists to review and review your responsiveness. With guaranteed
              response rates and negligible bounce rates, you can see if you're reaching your ideal
              demographic. Use the following strategies used by the top companies with the business
              insights we provide. Increase your success and start earning high profit margins today!
            </p>

            <div className="b2b-comp-strategy-grid">
              {CORE_STRATEGY_POINTS.map((item) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    className="b2b-comp-strategy-card"
                    variants={cardHover}
                    initial="initial"
                    whileHover="hover"
                  >
                    <div className="b2b-comp-strategy-card__icon">
                      <Icon size={22} />
                    </div>
                    <h4>{item.title}</h4>
                    <p>{item.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* Geo-Targeted Business Section */}
      <section className="b2b-comp-geo">
        <Container>
          <div className="b2b-comp-geo__box">
            <div className="b2b-comp-geo__icon-wrap">
              <MapPin size={32} />
            </div>
            <div className="b2b-comp-geo__text">
              <h2>Increase ROI with a Geo-Targeted Business Mailing List</h2>
              <p>
                Our B2B business geographic distribution list provides the location of the potential
                customer and their organizations and helps marketers connect with potential buyers
                from anywhere in the world. We proudly state that our lead database is the smartest
                way to leverage your marketing budget to grow your business and significantly
                increase sales.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Rational Business Email List Features */}
      <section className="b2b-comp-features">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Marketing Precision</span>
            <h2 className="section-title">
              A Rational Business Email Marketing List that Engenders Succeeding Leads!
            </h2>
            <p className="section-desc">
              Use our business email database to connect with potential consumers from over 50
              industries and to quadruple your quarterly sales. Our powerful business marketing list
              supports team efficiency and helps you enter new markets to create a seamless flow of
              sales.
            </p>
          </div>

          <div className="b2b-comp-features__grid">
            {EMAIL_LIST_ADVANTAGES.map((feature) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="b2b-comp-feature-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="b2b-comp-feature-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4>{feature.title}</h4>
                  <p>{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* 4-Step Campaign Improvement Process */}
      <section className="b2b-comp-workflow">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Execution Roadmap</span>
            <h2 className="section-title">
              How can you improve your marketing campaigns with our targeted email leads?
            </h2>
          </div>

          <div className="b2b-comp-steps__grid">
            {CAMPAIGN_WORKFLOW.map((stepItem) => (
              <motion.div
                key={stepItem.step}
                className="b2b-comp-step-card"
                whileHover={{ y: -6 }}
                transition={{ duration: 0.2 }}
              >
                <div className="b2b-comp-step-card__num">{stepItem.step}</div>
                <h4>{stepItem.title}</h4>
                <p>{stepItem.desc}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Bottom CTA */}
      <section className="b2b-comp-cta">
        <Container>
          <div className="b2b-comp-cta__box">
            <h2>Ready to Expand Your Account Footprint?</h2>
            <p>
              Speak with a Conectify Tech corporate intelligence consultant and acquire a customized
              B2B company dataset aligned with your revenue goals.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
              Request Your Custom Company Dataset
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}